import * as React from "react";

import * as ToastPrimitives from "@radix-ui/react-toast";
import { AlertTriangle, CheckCircle2, ClipboardCheck, X } from "lucide-react";

import { cn } from "../../lib/utils";

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      "fixed top-0 right-0 z-[100] flex max-h-screen w-full flex-col gap-3 p-4 md:max-w-[400px]",
      className,
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

export type ToastType = "neutral" | "success" | "error" | "warning";

const TYPE_STYLES: Record<
  ToastType,
  {
    accent: string;
    chipBg: string;
    iconColor: string;
    bar: string;
    actionColor: string;
    Icon: typeof ClipboardCheck;
  }
> = {
  neutral: {
    accent: "border-l-[var(--toast-neutral-accent)]",
    chipBg: "bg-[var(--toast-neutral-chip)]",
    iconColor: "text-[var(--toast-neutral-icon)]",
    bar: "bg-[var(--toast-neutral-accent)]",
    actionColor: "text-[var(--toast-neutral-accent)]",
    Icon: ClipboardCheck,
  },
  success: {
    accent: "border-l-[var(--toast-success-accent)]",
    chipBg: "bg-[var(--toast-success-chip)]",
    iconColor: "text-[var(--toast-success-icon)]",
    bar: "bg-[var(--toast-success-accent)]",
    actionColor: "text-[var(--toast-success-accent)]",
    Icon: CheckCircle2,
  },
  error: {
    accent: "border-l-[var(--toast-error-accent)]",
    chipBg: "bg-[var(--toast-error-chip)]",
    iconColor: "text-[var(--toast-error-icon)]",
    bar: "bg-[var(--toast-error-accent)]",
    actionColor: "text-[var(--toast-error-accent)]",
    Icon: AlertTriangle,
  },
  warning: {
    accent: "border-l-[var(--toast-warning-accent)]",
    chipBg: "bg-[var(--toast-warning-chip)]",
    iconColor: "text-[var(--toast-warning-icon)]",
    bar: "bg-[var(--toast-warning-accent)]",
    actionColor: "text-[var(--toast-warning-accent)]",
    Icon: AlertTriangle,
  },
};

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "rounded-sm text-sm font-medium text-current transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
      className,
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

interface ToastProps extends Omit<
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root>,
  "type" | "title"
> {
  type?: ToastType;
  title?: React.ReactNode;
  message?: React.ReactNode;
  action?: React.ReactNode;
  autoDismissMs?: number;
  icon?: typeof ClipboardCheck;
}

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  ToastProps
>(
  (
    {
      className,
      type = "neutral",
      title,
      message,
      action,
      autoDismissMs,
      icon,
      ...props
    },
    ref,
  ) => {
    const { accent, chipBg, iconColor, bar, actionColor, Icon } =
      TYPE_STYLES[type];
    // Errors always use one common icon; only neutral/success allow an override.
    const IconComp = type === "error" ? Icon : (icon ?? Icon);
    const hasAction = Boolean(action);
    const showProgress = autoDismissMs != null;
    const singleRow = !message && !hasAction;

    return (
      <ToastPrimitives.Root
        ref={ref}
        duration={autoDismissMs ?? Infinity}
        className={cn(
          "group pointer-events-auto relative w-full overflow-hidden rounded-[10px] border border-[var(--toast-border)] border-l-[3px] bg-[var(--toast-surface)] shadow-[var(--toast-shadow)]",
          accent,
          "data-[state=open]:[animation:toast-enter_240ms_cubic-bezier(0.2,0.8,0.2,1)] data-[state=closed]:[animation:toast-exit_120ms_ease-in_forwards]",
          "data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none",
          className,
        )}
        {...props}
      >
        <div
          className={cn(
            "flex gap-3 py-[12px] pl-[14px] pr-9",
            singleRow ? "items-center" : "items-start",
          )}
        >
          <span
            className={cn(
              "flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px]",
              chipBg,
              iconColor,
            )}
          >
            <IconComp className="h-4 w-4" strokeWidth={2} />
          </span>

          <div className="min-w-0 flex-1">
            {title && (
              <ToastPrimitives.Title className="text-sm font-semibold text-[var(--toast-title)]">
                {title}
              </ToastPrimitives.Title>
            )}
            {message && (
              <ToastPrimitives.Description className="mt-0.5 text-sm leading-snug text-[var(--toast-message)]">
                {message}
              </ToastPrimitives.Description>
            )}
            {hasAction && (
              <div className={cn("mt-2 flex items-center gap-3", actionColor)}>
                {action}
                <ToastPrimitives.Close className="rounded-sm text-sm font-medium text-[var(--toast-message)] transition-colors hover:text-[var(--toast-title)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                  Dismiss
                </ToastPrimitives.Close>
              </div>
            )}
          </div>
        </div>

        <ToastPrimitives.Close
          aria-label="Close"
          className="absolute right-3 top-3 rounded-sm text-[var(--toast-close)] transition-colors hover:text-[var(--toast-close-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <X className="h-4 w-4" />
        </ToastPrimitives.Close>

        {showProgress && (
          <span
            className={cn(
              "absolute bottom-0 left-0 h-[3px] w-full origin-left opacity-[0.55] [animation-name:toast-progress] [animation-timing-function:linear] [animation-fill-mode:forwards] group-hover:[animation-play-state:paused]",
              bar,
            )}
            style={{ animationDuration: `${autoDismissMs}ms` }}
          />
        )}
      </ToastPrimitives.Root>
    );
  },
);
Toast.displayName = ToastPrimitives.Root.displayName;

export { Toast, ToastAction, type ToastProps, ToastProvider, ToastViewport };
